import { useCallback, useEffect, useState } from 'react';

const usePdfSplitterSeam = (numPages) => {
  const [seamPage, setSeamPage] = useState(1);
  const [previewPage, setPreviewPage] = useState(null);
  const [trackedPages, setTrackedPages] = useState(numPages);

  if (numPages !== trackedPages) {
    setTrackedPages(numPages);
    setSeamPage(1);
    setPreviewPage(null);
  }

  const showPreviousPage = useCallback(() => {
    setSeamPage((page) => (page > 1 ? page - 1 : page));
  }, []);

  const showNextPage = useCallback(() => {
    setSeamPage((page) => (page < numPages ? page + 1 : page));
  }, [numPages]);

  const selectPage = useCallback((pageNumber) => {
    setSeamPage(() => {
      if (!numPages) return 1;
      return Math.min(Math.max(1, pageNumber), numPages);
    });
  }, [numPages]);

  const openPreview = useCallback((pageNumber) => {
    if (!numPages) return;
    setPreviewPage(Math.min(Math.max(1, pageNumber), numPages));
  }, [numPages]);

  const closePreview = useCallback(() => setPreviewPage(null), []);

  const showPreviewPrevious = useCallback(() => {
    setPreviewPage((page) => (page > 1 ? page - 1 : page));
  }, []);

  const showPreviewNext = useCallback(() => {
    setPreviewPage((page) => (page && page < numPages ? page + 1 : page));
  }, [numPages]);

  useEffect(() => {
    if (!numPages) return undefined;
    const handleKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (previewPage != null) {
        if (event.key === 'Escape') closePreview();
        else if (event.key === 'ArrowLeft') { event.preventDefault(); showPreviewPrevious(); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); showPreviewNext(); }
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousPage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextPage();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [numPages, previewPage, showPreviousPage, showNextPage, showPreviewPrevious, showPreviewNext, closePreview]);

  const page = numPages > 0 ? Math.min(Math.max(seamPage, 1), numPages) : null;

  const preview = numPages > 0 && previewPage != null
    ? Math.min(Math.max(previewPage, 1), numPages)
    : null;

  return {
    seamPage: page,
    selectPage,
    showPreviousPage,
    showNextPage,
    previewPage: preview,
    openPreview,
    closePreview,
    showPreviewPrevious,
    showPreviewNext,
  };
};

export default usePdfSplitterSeam;
